import { useMemo, useState } from "react";
import { Crop, Scale } from "lucide-react";

const IN_TO_MM = 25.4;
const fmt = (n, d = 3) => (Number.isFinite(n) ? n.toFixed(d).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "") : "—");

const PRESETS = [
  { label: "Business card 3.5 × 2", w: 3.5, h: 2 },
  { label: "Postcard 4 × 6", w: 4, h: 6 },
  { label: "Postcard 5 × 7", w: 5, h: 7 },
  { label: "Flyer 8.5 × 11", w: 8.5, h: 11 },
  { label: "Half-fold 11 × 8.5", w: 11, h: 8.5 },
  { label: "Poster 18 × 24", w: 18, h: 24 },
  { label: "Retractable 33 × 81", w: 33, h: 81 },
];

const field = "bg-black/30 border border-white/15 rounded-sm px-2.5 py-2 text-sm text-white font-mono outline-none focus:border-[#B87333] w-full";
const label = "block font-mono text-[10px] tracking-[0.2em] uppercase text-white/60 mb-1";

export function BleedCalculator() {
  const [unit, setUnit] = useState("in");
  const [w, setW] = useState(3.5);
  const [h, setH] = useState(2);
  const [bleed, setBleed] = useState(0.125);
  const [safe, setSafe] = useState(0.125);
  const [dpi, setDpi] = useState(300);

  const r = useMemo(() => {
    const k = unit === "mm" ? 1 / IN_TO_MM : 1;
    const W = w * k, H = h * k, B = bleed * k, S = safe * k;
    return {
      docW: W + 2 * B, docH: H + 2 * B,
      safeW: W - 2 * S, safeH: H - 2 * S,
      pxW: Math.round((W + 2 * B) * dpi), pxH: Math.round((H + 2 * B) * dpi),
      trimPxW: Math.round(W * dpi), trimPxH: Math.round(H * dpi),
    };
  }, [unit, w, h, bleed, safe, dpi]);

  const show = (inches) => (unit === "mm" ? `${fmt(inches * IN_TO_MM, 1)} mm` : `${fmt(inches)}"`);
  const applyPreset = (e) => {
    const p = PRESETS[e.target.value];
    if (!p) return;
    setUnit("in"); setW(p.w); setH(p.h);
  };
  const setUnitConvert = (u) => {
    if (u === unit) return;
    const f = u === "mm" ? IN_TO_MM : 1 / IN_TO_MM;
    setW(+(w * f).toFixed(2)); setH(+(h * f).toFixed(2)); setBleed(+(bleed * f).toFixed(3)); setSafe(+(safe * f).toFixed(3)); setUnit(u);
  };

  return (
    <div className="bg-[#1F2830] text-white rounded-sm p-5 md:p-6 my-4 not-prose" data-testid="bleed-calculator">
      <div className="flex items-center gap-2 mb-4 font-mono text-[11px] tracking-[0.24em] uppercase font-bold"><Crop className="w-4 h-4 text-[#B87333]" /> Bleed &amp; safe-zone calculator</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="col-span-2">
          <label className={label}>Preset</label>
          <select className={field} onChange={applyPreset} defaultValue="" data-testid="bleed-preset">
            <option value="">Custom size…</option>
            {PRESETS.map((p, i) => <option key={p.label} value={i}>{p.label}</option>)}
          </select>
        </div>
        <div><label className={label}>Trim width</label><input type="number" step="0.01" min="0" value={w} onChange={(e) => setW(+e.target.value)} className={field} data-testid="bleed-width" /></div>
        <div><label className={label}>Trim height</label><input type="number" step="0.01" min="0" value={h} onChange={(e) => setH(+e.target.value)} className={field} data-testid="bleed-height" /></div>
        <div><label className={label}>Bleed each side</label><input type="number" step="0.001" min="0" value={bleed} onChange={(e) => setBleed(+e.target.value)} className={field} data-testid="bleed-bleed" /></div>
        <div><label className={label}>Safe margin</label><input type="number" step="0.001" min="0" value={safe} onChange={(e) => setSafe(+e.target.value)} className={field} data-testid="bleed-safe" /></div>
        <div>
          <label className={label}>Units</label>
          <div className="flex">
            {["in", "mm"].map((u) => (
              <button key={u} onClick={() => setUnitConvert(u)} className={`flex-1 py-2 font-mono text-xs border border-white/15 ${unit === u ? "bg-[#B87333] text-white" : "bg-black/30 text-white/70"}`} data-testid={`bleed-unit-${u}`}>{u}</button>
            ))}
          </div>
        </div>
        <div><label className={label}>Resolution</label>
          <select value={dpi} onChange={(e) => setDpi(+e.target.value)} className={field} data-testid="bleed-dpi">
            {[300, 150, 100, 72].map((d) => <option key={d} value={d}>{d} DPI</option>)}
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        {[
          ["Document size (with bleed)", `${show(r.docW)} × ${show(r.docH)}`, "Set your artboard / page size to this", "bleed-result-doc"],
          ["Trim size", `${show(w * (unit === "mm" ? 1 / IN_TO_MM : 1))} × ${show(h * (unit === "mm" ? 1 / IN_TO_MM : 1))}`, "Final cut size — place crop marks here", "bleed-result-trim"],
          ["Safe zone", `${show(r.safeW)} × ${show(r.safeH)}`, "Keep text & logos inside this box", "bleed-result-safe"],
        ].map(([t, v, s, id]) => (
          <div key={t} className="bg-black/30 rounded-sm p-3">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">{t}</div>
            <div className="font-display font-black text-xl text-[#E8B587]" data-testid={id}>{v}</div>
            <div className="text-[11px] text-white/60 mt-1">{s}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-white/70 mt-4" data-testid="bleed-result-px">
        Raster export at {dpi} DPI: <span className="font-mono text-white">{r.pxW} × {r.pxH} px</span> with bleed (trim {r.trimPxW} × {r.trimPxH} px). Kolt standard: 0.125" bleed and 0.125" safe margin on all products; 0.25" bleed and 1" safe on banners and large format.
      </p>
    </div>
  );
}

const BASIS = {
  text: { label: "Text / Book / Offset (25 × 38)", factor: 1.4802 },
  cover: { label: "Cover (20 × 26)", factor: 2.7042 },
  bond: { label: "Bond / Writing (17 × 22)", factor: 3.7597 },
  index: { label: "Index (25.5 × 30.5)", factor: 1.8113 },
  tag: { label: "Tag (24 × 36)", factor: 1.6284 },
  bristol: { label: "Bristol (22.5 × 28.5)", factor: 2.1971 },
};

export function PaperWeightConverter() {
  const [value, setValue] = useState(100);
  const [from, setFrom] = useState("lb");
  const [basis, setBasis] = useState("cover");

  const gsm = from === "gsm" ? value : value * BASIS[basis].factor;
  const rows = Object.entries(BASIS).map(([k, b]) => [b.label, gsm / b.factor]);
  const ptApprox = gsm * 0.04; // coated cover rule of thumb: 14pt ≈ 350 gsm

  return (
    <div className="bg-[#1F2830] text-white rounded-sm p-5 md:p-6 my-4 not-prose" data-testid="paper-weight-converter">
      <div className="flex items-center gap-2 mb-4 font-mono text-[11px] tracking-[0.24em] uppercase font-bold"><Scale className="w-4 h-4 text-[#B87333]" /> Paper weight converter · gsm ↔ lb</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className={label}>Weight</label><input type="number" min="1" step="1" value={value} onChange={(e) => setValue(+e.target.value)} className={field} data-testid="pw-value" /></div>
        <div>
          <label className={label}>Unit</label>
          <div className="flex">
            {["lb", "gsm"].map((u) => (
              <button key={u} onClick={() => setFrom(u)} className={`flex-1 py-2 font-mono text-xs border border-white/15 ${from === u ? "bg-[#B87333] text-white" : "bg-black/30 text-white/70"}`} data-testid={`pw-unit-${u}`}>{u}</button>
            ))}
          </div>
        </div>
        <div className={`col-span-2 ${from === "gsm" ? "opacity-40 pointer-events-none" : ""}`}>
          <label className={label}>Basis (for lb)</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value)} className={field} data-testid="pw-basis">
            {Object.entries(BASIS).map(([k, b]) => <option key={k} value={k}>{b.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <div className="bg-black/30 rounded-sm p-3">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">Grams per square metre</div>
          <div className="font-display font-black text-2xl text-[#E8B587]" data-testid="pw-result-gsm">{fmt(gsm, 0)} gsm</div>
          <div className="text-[11px] text-white/60 mt-1">≈ {fmt(ptApprox, 0)} pt caliper on uncoated cover stock</div>
        </div>
        <div className="bg-black/30 rounded-sm p-3">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/60">Equivalent basis weights</div>
          <ul className="text-sm mt-1 space-y-0.5" data-testid="pw-result-rows">
            {rows.map(([l, v]) => <li key={l} className="flex justify-between gap-3"><span className="text-white/70">{l.split(" (")[0]}</span><span className="font-mono">{fmt(v, 0)} lb</span></li>)}
          </ul>
        </div>
      </div>
      <p className="text-xs text-white/70 mt-4">Common picks: 100# gloss text ≈ 148 gsm · 14pt cover ≈ 350 gsm (130# cover) · 16pt ≈ 400 gsm · GF Smith Colorplan 540 gsm ≈ 200# cover.</p>
    </div>
  );
}
